<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { computed, onBeforeUnmount, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'
import AppButton from '@uiKit/elements/Button.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import {
  AccountRestrictionNotice,
  KycActionBlocker,
  KycReviewNotice,
  type KycActionState,
} from '@modules/account'
import { canTransactInWallet } from '../utils/walletAccessUtils'

interface Props {
  currency?: string
  /** Formatted balance, e.g. `"50,745.00"`. */
  balance?: string
  lastUpdated?: string
  loading?: boolean
  hasError?: boolean
  /** Account access restricted (blocked investor) — beats every other gate. */
  restricted?: boolean
  kycState?: KycActionState
  /** At least one linked bank account, any verification status. */
  hasBankAccount?: boolean
  /** At least one *verified* bank account. */
  hasActiveBank?: boolean
  refreshing?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  currency: 'AED',
  balance: '',
  lastUpdated: '',
  loading: false,
  hasError: false,
  restricted: false,
  kycState: 'verified',
  hasBankAccount: false,
  hasActiveBank: false,
  refreshing: false,
})

const emit = defineEmits<{
  (e: 'add-funds'): void
  (e: 'withdraw'): void
  (e: 'add-bank'): void
  (e: 'refresh'): void
}>()

// A cached balance can come back in ~30ms, which makes the spinner flash and
// read as a glitch rather than "I refreshed it". Hold the spin briefly so the
// investor always sees that the tap did something.
const MIN_SPIN_MS = 600

const spinHold = ref(false)
let spinTimer: ReturnType<typeof setTimeout> | null = null

onBeforeUnmount(() => {
  if (spinTimer) clearTimeout(spinTimer)
})

const isSpinning = computed(() => props.refreshing || spinHold.value)

const onRefresh = () => {
  if (isSpinning.value) return
  spinHold.value = true
  if (spinTimer) clearTimeout(spinTimer)
  spinTimer = setTimeout(() => {
    spinHold.value = false
  }, MIN_SPIN_MS)
  emit('refresh')
}

const canTransact = computed(() =>
  canTransactInWallet({
    restricted: props.restricted,
    kycState: props.kycState,
    hasBankAccount: props.hasBankAccount,
    hasActiveBank: props.hasActiveBank,
  }),
)

// Restriction and KYC blockers replace the action row with an explanation.
// Everything else falls through to either the two CTAs or the "add a bank"
// nudge, so the investor always sees why money can't move rather than a
// silently disabled button.
const blocker = computed<'restricted' | 'kyc-action' | 'kyc-review' | null>(() => {
  if (props.restricted) return 'restricted'
  if (props.kycState === 'action-required') return 'kyc-action'
  if (props.kycState === 'in-review' && !props.hasActiveBank) return 'kyc-review'
  return null
})
</script>

<template>
  <Box is="section" align="center" justify="between" gap="4" class="wbc">
    <Box direction="col" gap="1" grow class="wbc__left">
      <Box align="center" gap="3" class="wbc__label-row">
        <Text type="body" weight="bold" color="muted" transform="uppercase" is="span">
          Available Balance
        </Text>
        <Box
          is="button"
          flex
          align="center"
          justify="center"
          class="wbc__refresh"
          type="button"
          :disabled="isSpinning"
          aria-label="Refresh balance"
          title="Refresh balance"
          @click="onRefresh"
        >
          <PiIcon
            name="refresh"
            :class="['wbc__refresh-icon', { 'wbc__refresh-icon--spinning': isSpinning }]"
          />
        </Box>
      </Box>

      <template v-if="hasError">
        <Text type="title-lg" color="danger" is="p">Couldn't load balance</Text>
        <Box align="center" gap="2">
          <Text type="body" color="muted" is="span">Check your connection and try again.</Text>
          <AppButton
            label="Retry"
            variant="text"
            color="primary"
            size="small"
            @click="emit('refresh')"
          />
        </Box>
      </template>

      <template v-else-if="loading">
        <Skeleton width="180px" height="29px" />
        <Skeleton width="140px" height="18px" />
      </template>

      <template v-else>
        <Text type="title-lg" is="p" class="wbc__amount">
          <CurrencyText :text="`${currency} ${balance || '0.00'}`" />
        </Text>
        <Text v-if="lastUpdated" type="body" color="muted" is="p">
          Last updated {{ lastUpdated }}
        </Text>
      </template>
    </Box>

    <Box v-if="blocker" class="wbc__notice">
      <AccountRestrictionNotice
        v-if="blocker === 'restricted'"
        description="Adding funds and withdrawals are unavailable while your account access is restricted. Please contact support for more information."
      />
      <KycActionBlocker
        v-else-if="blocker === 'kyc-action'"
        description="Adding funds and withdrawals are unavailable until your identity is verified. Complete your KYC to start transacting."
      />
      <KycReviewNotice v-else />
    </Box>

    <Box v-else align="center" gap="3" class="wbc__actions">
      <template v-if="canTransact">
        <AppButton label="Add Funds" icon="plus" class="wbc__action" @click="emit('add-funds')" />
        <AppButton
          label="Withdraw"
          icon="arrows-h"
          color="secondary"
          variant="outline"
          class="wbc__action"
          @click="emit('withdraw')"
        />
      </template>
      <AppButton
        v-else
        label="Add bank account"
        icon="plus"
        class="wbc__action"
        @click="emit('add-bank')"
      />
    </Box>
  </Box>
</template>

<style scoped>
.wbc {
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: 16px;
  padding: 1.625rem 1.75rem;
  box-sizing: border-box;
}

.wbc__left {
  min-width: 0;
}

.wbc__label-row {
  min-height: 29px;
}

.wbc__amount {
  font-variant-numeric: tabular-nums;
}

.wbc__refresh {
  width: 33px;
  height: 29px;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: 10px;
  cursor: pointer;
  color: var(--p-text-muted-color);
  flex-shrink: 0;
  transition:
    background 0.15s,
    color 0.15s;
}

.wbc__refresh:hover:not(:disabled) {
  background: var(--p-primary-50);
  color: var(--p-primary-600);
}

.wbc__refresh:disabled {
  cursor: default;
}

.wbc__refresh:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.wbc__refresh-icon {
  font-size: 14px;
}

.wbc__refresh-icon--spinning {
  animation: wbc-spin 0.8s linear infinite;
}

@keyframes wbc-spin {
  to {
    transform: rotate(360deg);
  }
}

.wbc__actions {
  flex-shrink: 0;
}

.wbc__action {
  min-width: 148px;
}

.wbc__notice {
  flex-shrink: 0;
  max-width: 420px;
}

@media (max-width: 992px) {
  .wbc {
    flex-direction: column;
    align-items: stretch;
    padding: 1.25rem 1rem;
  }

  .wbc__actions,
  .wbc__notice {
    width: 100%;
    max-width: none;
    margin-top: 0.25rem;
  }

  .wbc__action {
    flex: 1;
    min-width: 0;
  }
}
</style>
